import React, { useState, useRef, useEffect } from 'react';
import { InlineWidget } from 'react-calendly';
import { companyInfo } from '../mock';
import { Mail, Phone, Send } from 'lucide-react';

// Custom Dropdown Component with glass styling
const CustomSelect = ({ value, onChange, options, id, name }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const selectedLabel = options.find(opt => opt.value === value)?.label || 'Select...';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-left flex justify-between items-center hover:border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
      >
        <span>{selectedLabel}</span>
        <svg className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 border border-white/10 rounded-xl shadow-2xl z-50 bg-slate-900/95">
          {options.map((option, index) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange({ target: { name, value: option.value } });
                setIsOpen(false);
              }}
              className={`w-full px-4 py-3 text-left font-medium transition-colors ${
                index === 0 ? 'rounded-t-[10px]' : ''
              } ${
                index === options.length - 1 ? 'rounded-b-[10px]' : ''
              } ${
                value === option.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-900/95 text-white hover:bg-slate-800'
              } ${index > 0 ? 'border-t border-white/5' : ''}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ContactSection = ({ onBooking }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: 'lead-generation',
    message: ''
  });

  const [showCalendly, setShowCalendly] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.07 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const fadeClass = (extra = '') => isVisible ? `animate-fade-up ${extra}`.trim() : 'opacity-0';

  const serviceOptions = [
    { value: 'lead-generation', label: 'Lead Generation' },
    { value: 'sales-funnels', label: 'Sales Funnels' },
    { value: 'crm-ai-automation', label: 'CRM & AI Automation' },
    { value: 'web-development', label: 'Web Development' },
    { value: 'full-package', label: 'Full Service Package' }
  ];

  const handleCalendlyEventScheduled = () => {
    // Redirect to thank you page after successful scheduling
    setTimeout(() => {
      const thankYouSection = document.querySelector('#thank-you');
      if (thankYouSection) {
        thankYouSection.scrollIntoView({ behavior: 'smooth' });
      } else {
        // If thank you section doesn't exist, scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 500);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage({ type: '', text: '' });

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitMessage({
        type: 'success',
        text: 'Thank you! Please select a time for your consultation below.'
      });
      setShowCalendly(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: 'lead-generation',
        message: ''
      });
    }, 1000);
  };

  return (
    <section ref={sectionRef} id="contact" className="py-32 px-6 overflow-hidden">
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-up {
          animation: fadeInUp 0.8s ease-out forwards;
          opacity: 0;
        }
        .delay-100 {
          animation-delay: 0.5s;
        }
        .delay-200 {
          animation-delay: 0.6s;
        }
        .delay-300 {
          animation-delay: 0.7s;
        }
        .delay-400 {
          animation-delay: 0.8s;
        }
        .input-animate {
          transition: all 0.3s ease;
        }
        .input-animate:focus {
          transform: translateY(-2px);
          box-shadow: 0 0 20px rgba(0, 123, 255, 0.2);
        }
      `}</style>
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className={`mb-16 ${fadeClass()}`}>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#007bff] mb-3">Get In Touch</p>
          <h2 className={`text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight leading-tight ${fadeClass('delay-100')}`}>
            Let's Build Your<br />Growth System
          </h2>
          <p className={`text-gray-400 text-lg max-w-xl ${fadeClass('delay-200')}`}>
            Book a free consultation and get a custom growth audit for your business. No obligation, no hard sell — just a clear roadmap to predictable revenue.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className={`glass p-8 rounded-3xl ${fadeClass('delay-100')}`}>
              <h3 className="text-xl font-bold mb-6 text-white">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-600/10 rounded-2xl">
                    <Mail className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-white mb-1">Email</p>
                      <a href={`mailto:${companyInfo.email}`} className="text-gray-400 hover:text-blue-400 transition-colors text-sm">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>

              </div>
            </div>

            {/* Quick CTA */}
            <div className={`glass p-8 rounded-3xl text-center border-blue-500/30 ${fadeClass('delay-200')}`}>
              <p className="text-gray-400 mb-4">Skip the form?</p>
              <button
                onClick={onBooking}
                className="bg-blue-600 text-white font-bold px-8 py-3 rounded-xl glow-button hover-lift w-full"
              >
                Book Directly
              </button>
            </div>
          </div>

          {/* Contact Form or Calendly */}
          <div className="lg:col-span-2">
            {!showCalendly ? (
              <form onSubmit={handleSubmit} className={`glass p-8 md:p-12 rounded-3xl space-y-6 ${fadeClass('delay-300')}`}>
                {submitMessage.text && (
                  <div
                    className={`p-4 rounded-xl ${
                      submitMessage.type === 'success'
                        ? 'bg-blue-600/20 text-blue-300 border border-blue-500/30'
                        : 'bg-red-600/20 text-red-300 border border-red-500/30'
                    }`}
                  >
                    {submitMessage.text}
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-white mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-white placeholder-gray-500 input-animate"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-white mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-white placeholder-gray-500 input-animate"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-white mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-white placeholder-gray-500 input-animate"
                      placeholder="+91 98765 43210"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-white mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-white placeholder-gray-500 input-animate"
                      placeholder="Your Company"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-semibold text-white mb-2">
                    Service Interested In *
                  </label>
                  <CustomSelect
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    options={serviceOptions}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-white mb-2">
                    What Problem Are You Looking to Solve? *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none text-white placeholder-gray-500 input-animate"
                    placeholder="Tell us about your current lead generation challenges, revenue goals, or what's holding your business back..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-blue-600 text-white font-black px-8 py-4 rounded-xl glow-button w-full inline-flex items-center gap-2 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span>Submitting...</span>
                  ) : (
                    <>
                      <span>Submit & Book Consultation</span>
                      <Send className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            ) : (
              <div className="glass p-8 rounded-3xl animate-fade-up delay-300">
                <h3 className="text-2xl font-bold mb-6 text-white text-center">
                  Select Your Preferred Time
                </h3>
                <InlineWidget
                  url={companyInfo.calendlyLink}
                  styles={{
                    height: '700px',
                    minWidth: '320px'
                  }}
                  pageSettings={{
                    backgroundColor: '020617',
                    hideEventTypeDetails: false,
                    hideLandingPageDetails: false,
                    primaryColor: '3B82F6',
                    textColor: 'ffffff'
                  }}
                  prefill={{
                    name: formData.name || '',
                    email: formData.email || '',
                    customAnswers: {
                      a1: formData.company || '',
                      a2: formData.service || ''
                    }
                  }}
                  utm={{
                    utmSource: 'website',
                    utmCampaign: 'contact_form',
                    utmContent: 'inline_booking'
                  }}
                  onEventScheduled={handleCalendlyEventScheduled}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;